import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  MagnifyingGlass, 
  Funnel, 
  X,
  Truck,
  Bus,
  Van,
  Warning,
  ArrowRight,
  SpinnerGap
} from '@phosphor-icons/react';
import axios from 'axios';
import { BookmarkButton } from '../components/BookmarkButton';
import { OGLAttribution } from '../components/OGLAttribution';

const API = process.env.REACT_APP_BACKEND_URL;

const getSeverityClass = (severity) => {
  switch (severity?.toUpperCase()) {
    case 'MSI':
      return 'severity-serious';
    case 'VSI':
      return 'severity-significant';
    case 'SI':
      return 'severity-significant';
    case 'STANDARD':
      return 'severity-minor';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const getSeverityLabel = (severity) => {
  switch (severity?.toUpperCase()) {
    case 'MSI':
      return 'Most Serious';
    case 'VSI':
      return 'Very Serious';
    case 'SI':
      return 'Serious';
    case 'STANDARD':
      return 'Standard';
    default:
      return severity || 'Unknown';
  }
};

const getVehicleIcon = (type) => {
  switch (type?.toUpperCase()) {
    case 'HGV':
      return <Truck weight="duotone" className="h-4 w-4" />;
    case 'PSV':
      return <Bus weight="duotone" className="h-4 w-4" />;
    case 'LGV':
      return <Van weight="duotone" className="h-4 w-4" />;
    default:
      return <Truck weight="duotone" className="h-4 w-4" />;
  }
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [sanctions, setSanctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [vehicleType, setVehicleType] = useState(searchParams.get('vehicle_type') || '');
  const [severity, setSeverity] = useState(searchParams.get('severity') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  
  const [categories, setCategories] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sanctions/categories/list`);
      setCategories(data.categories || []);
      setVehicleTypes(data.vehicle_types || []);
      setSeverities(data.severities || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchSanctions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (vehicleType) params.append('vehicle_type', vehicleType);
      if (severity) params.append('severity', severity);
      if (category) params.append('category', category);
      
      const { data } = await axios.get(`${API}/api/sanctions/?${params.toString()}`);
      setSanctions(data.sanctions || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching sanctions:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, vehicleType, severity, category]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchSanctions();
      
      // Update URL params
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (vehicleType) params.set('vehicle_type', vehicleType);
      if (severity) params.set('severity', severity);
      if (category) params.set('category', category);
      setSearchParams(params, { replace: true });
    }, 300);
    
    return () => clearTimeout(debounce);
  }, [searchQuery, vehicleType, severity, category, fetchSanctions, setSearchParams]);

  const clearFilters = () => {
    setSearchQuery('');
    setVehicleType('');
    setSeverity('');
    setCategory('');
  };

  const hasActiveFilters = searchQuery || vehicleType || severity || category;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Sanctions Database</h1>
          <p className="text-muted-foreground">Search and filter DVSA enforcement sanctions (May 2026 policy)</p>
        </div>
        
        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search offences, categories, outcomes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 rounded-sm text-base"
                data-testid="search-input"
              />
            </div>
            <Button
              variant={showFilters ? 'secondary' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 px-4 rounded-sm"
              data-testid="toggle-filters-btn"
            >
              <Funnel weight="duotone" className="h-5 w-5 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                  {[vehicleType, severity, category].filter(Boolean).length}
                </span>
              )}
            </Button>
          </div>
          
          {/* Filter Dropdowns */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 border border-border bg-muted/30">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-2 block">
                  Vehicle Type
                </label>
                <Select value={vehicleType} onValueChange={(v) => setVehicleType(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="vehicle-type-filter">
                    <SelectValue placeholder="All vehicles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All vehicles</SelectItem>
                    {vehicleTypes.map((vt) => (
                      <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-2 block">
                  Severity
                </label>
                <Select value={severity} onValueChange={(v) => setSeverity(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="severity-filter">
                    <SelectValue placeholder="All severities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All severities</SelectItem>
                    {severities.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-2 block">
                  Category
                </label>
                <Select value={category} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="category-filter">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          {/* Active Filters & Clear */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary" className="rounded-sm">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {vehicleType && (
                <Badge variant="secondary" className="rounded-sm">
                  {vehicleType}
                  <button onClick={() => setVehicleType('')} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {severity && (
                <Badge variant="secondary" className="rounded-sm">
                  {severity}
                  <button onClick={() => setSeverity('')} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {category && (
                <Badge variant="secondary" className="rounded-sm">
                  {category}
                  <button onClick={() => setCategory('')} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                Clear all
              </Button>
            </div>
          )}
        </div>
        
        {/* Results Count */}
        <div className="mb-4 text-sm text-muted-foreground">
          {loading ? 'Searching...' : `${total} sanction${total !== 1 ? 's' : ''} found`}
        </div>
        
        {/* Results List */}
        <div className="border border-border divide-y divide-border">
          {loading ? (
            <div className="p-12 text-center">
              <SpinnerGap weight="bold" className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : sanctions.length === 0 ? (
            <div className="p-12 text-center">
              <Warning weight="duotone" className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No sanctions found matching your criteria.</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            sanctions.map((sanction, index) => (
              <div
                key={sanction.id}
                className="relative flex items-start gap-2 hover:bg-muted/50 transition-colors duration-150 animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="pt-4 sm:pt-6 pl-2 sm:pl-4">
                  <BookmarkButton sanctionId={sanction.id} />
                </div>
                <Link
                  to={`/sanctions/${sanction.id}`}
                  className="block flex-1 p-4 sm:p-6 pl-1"
                  data-testid={`sanction-item-${sanction.id}`}
                >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg mb-2 pr-4">{sanction.offence_title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{sanction.description}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getSeverityClass(sanction.severity)}`}>
                        {getSeverityLabel(sanction.severity)}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                        {getVehicleIcon(sanction.vehicle_type)}
                        {sanction.vehicle_type}
                      </span>
                      <span className="text-xs text-muted-foreground">{sanction.category}</span>
                    </div>
                  </div>
                  <ArrowRight weight="bold" className="h-5 w-5 text-muted-foreground flex-shrink-0 hidden sm:block" />
                </div>
                </Link>
              </div>
            ))
          )}
        </div>
      </main>
      
      {/* Footer Disclaimer */}
      <footer className="border-t border-border mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-3">
          <p className="text-xs text-muted-foreground text-center">
            This tool is for guidance only and does not constitute legal advice. Based on DVSA Enforcement Sanctions Policy (May 2026).
          </p>
          <OGLAttribution className="text-center" />
        </div>
      </footer>
    </div>
  );
};
